import { Piggyback, TaskTracking } from '../async/taskTracking';
import type { SegmentInfo, SegmentProvider } from '../types/segmentProvider';
import type { EncodedData } from '../mpegts/demuxerTypes';
import type { Fraction } from '../util/fractionType';
import type { VirtualClipInfo } from '../dto/playableItemDto';
import type { PlaybackQuality, QualityOption } from '../types/playbackQuality';
import { invariant } from '../util/invariant';

/**
 *  This SegmentProvider wrapper optimizes use of one segment provider from
 *  multiple green threads. If one green thread is already running on async
 *  operation on this wrapper then another call to the same function with the
 *  same parameters will piggy pack on the current ongoing operation.
 *
 *  Note that this class in itself does not implement any form of reference
 *  counting. This must be handled externally. Instead you probably want to
 *  use the CachedSegmentProvider which does both caching and uses the shared
 *  segment provider internally to optimize calls to the same underlying
 *  segment provider.
 */
export class SharedSegmentProvider implements SegmentProvider {
  private innerProvider: SegmentProvider;
  private taskTrackingGetSegmentInfoAt = new TaskTracking<SegmentInfo | null>();
  private taskTrackingGetSegment = new TaskTracking<EncodedData>();
  private piggyBackGetDuration = new Piggyback<Fraction>();
  private piggyBackGetVirtualClipInfo = new Piggyback<VirtualClipInfo | null>();
  private piggyBackGetCurrentQuality = new Piggyback<PlaybackQuality>();
  private piggyBackGetAvailableQualities = new Piggyback<QualityOption[]>();

  constructor(provider: SegmentProvider) {
    this.innerProvider = provider;
  }

  public isValid(): boolean {
    return this.innerProvider !== null;
  }

  async getSegmentInfoAt(position: Fraction): Promise<SegmentInfo | null> {
    const id = `${position.numerator}:${position.denominator}`;
    const info = await this.taskTrackingGetSegmentInfoAt.runOrPiggyback(id, () => this.innerProvider.getSegmentInfoAt(position));
    return info;
  }

  async getSegment(id: string): Promise<EncodedData> {
    return await this.taskTrackingGetSegment.runOrPiggyback(id, () => this.innerProvider.getSegment(id));
  }

  async getDuration(): Promise<Fraction> {
    return await this.piggyBackGetDuration.runOrPiggyback(() => this.innerProvider.getDuration());
  }

  async getVirtualClipInfo(): Promise<VirtualClipInfo | null> {
    return await this.piggyBackGetVirtualClipInfo.runOrPiggyback(() => this.innerProvider.getVirtualClipInfo());
  }

  async getCurrentQuality(): Promise<PlaybackQuality> {
    return await this.piggyBackGetCurrentQuality.runOrPiggyback(() => this.innerProvider.getCurrentQuality());
  }

  async getAvailableQualities(): Promise<QualityOption[]> {
    return await this.piggyBackGetAvailableQualities.runOrPiggyback(() => this.innerProvider.getAvailableQualities());
  }

  addDurationChangedListener(onDurationChanged: (duration: Fraction) => void): () => void {
    const provider = this.innerProvider;
    invariant(provider, 'Use of closed segment provider');
    return this.innerProvider?.addDurationChangedListener(onDurationChanged);
  }

  close(): void {
    this.innerProvider.close();
  }
}
